import React, { ButtonHTMLAttributes } from 'react';
import { IconElements } from '../../config/icons';

/** Neutral meaning white when white theme and black when black theme */
export type ButtonTypes = 'primary' | 'secondary' | 'internalLinkBlack' | 'internalLinkGreen';

export type WobbleArrowType = 'left' | 'right' | undefined;

const getWobbleArrowIcon = (wobbleArrowType: WobbleArrowType, buttonType: ButtonTypes) => {
    const textCol = getTextColorClass(buttonType);
    const classNameArrow = 'ml-2 group-hover:animate-bouncex w-4 h-4';

    return wobbleArrowType === 'left' ? (
        <IconElements.ArrowLeft className={`${textCol} ${classNameArrow}`} />
    ) : wobbleArrowType === 'right' ? (
        <IconElements.ArrowRight className={`${textCol} ${classNameArrow}`} />
    ) : undefined;
};

const getBackgroundColorClass = (buttonType: ButtonTypes) => {
    switch (buttonType) {
        case 'primary':
            return 'bg-bnd-button hover:bg-bnd-button-hover disabled:bg-bnd-black-30 transition';
        case 'secondary':
            return 'bg-white';
        default:
            return '';
    }
};

const getTextColorClass = (buttonType: ButtonTypes) => {
    switch (buttonType) {
        case 'primary':
            return 'text-white';
        case 'secondary':
            return 'text-bnd-black-80 hover:text-bnd-black disabled:text-bnd-black-30';
        case 'internalLinkBlack':
            return 'text-bnd-black-80 hover:text-bnd-black';
        case 'internalLinkGreen':
            return 'text-bnd-green hover:text-bnd-green-hover';
    }
};

const getBorderClass = (buttonType: ButtonTypes) => {
    switch (buttonType) {
        case 'secondary':
            return 'border border-bnd-black-80 hover:border-bnd-black disabled:border-bnd-black-30 py-1.75 px-3.75 transition';
        case 'primary':
            return 'border-0 py-2 px-4';
        default:
            return 'border-0';
    }
};

const getShadowClass = (buttonType: ButtonTypes) => {
    switch (buttonType) {
        case 'primary':
            return 'shadow-button disabled:shadow-none';
        case 'secondary':
            return 'shadow-button disabled:shadow-none';
        default:
            return '';
    }
};

export const Button: React.FC<
    {
        isLoading?: boolean;
        isDisabled?: boolean;
        showWobbleArrow?: boolean;
        wobbleArrowType?: WobbleArrowType;
        buttonType: ButtonTypes;
    } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'size'>
> = ({
    className,
    children,
    isDisabled = false,
    showWobbleArrow = false,
    wobbleArrowType = 'right',
    id,
    buttonType = 'internalLinkGreen',
    ...rest
}) => {
    return (
        <button
            className={`group block relative text-center truncate focus:outline-hidden leading-6 rounded ${getShadowClass(
                buttonType,
            )} ${getBorderClass(buttonType)} ${getTextColorClass(buttonType)} ${getBackgroundColorClass(
                buttonType,
            )} ${className} ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            id={id}
            disabled={isDisabled}
            {...rest}
        >
            <div className={`flex flex-row gap-x-2 justify-center items-center`}>
                <>
                    <div style={{ display: showWobbleArrow ? 'block' : 'none' }}>
                        {getWobbleArrowIcon(wobbleArrowType, buttonType)}
                    </div>
                    {children}
                </>
            </div>
        </button>
    );
};
